import { Badge, Box, Group, Loader, SimpleGrid, Stack, Table, Text, Title } from "@mantine/core";
import { useParams } from "@tanstack/react-router";
import { useRef, useState } from "react";

import { useGetStaffTeam } from "../../api/generated/endpoints/staff/staff";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { formatDate } from "../../ui/format";
import { AdjustForm, CancelJobForm, GrantForm } from "./CreditForms";
import { LedgerTable } from "./LedgerTable";

/** One team as staff sees it (A7): members, invitations, balance and recent ledger entries. */
export function StaffTeamScreen() {
  const { teamId } = useParams({ from: "/signed-in/staff/teams/$teamId" });
  const detail = useGetStaffTeam(teamId);
  // "Cancel this job" on a ledger entry: a fresh cancel form with that job, brought into view.
  const [cancelJob, setCancelJob] = useState("");
  const cancelForm = useRef<HTMLDivElement>(null);
  const toCancel = (jobId: string) => {
    setCancelJob(jobId);
    cancelForm.current?.scrollIntoView?.({ block: "center" });
  };
  const cancel = (
    <Box ref={cancelForm}>
      <CancelJobForm key={cancelJob} teamId={teamId} initialJobId={cancelJob} />
    </Box>
  );
  if (detail.isPending) return <Loader size="sm" aria-label="Loading the team" />;
  if (detail.isError) return <ErrorAlert error={detail.error} />;
  const { team, members, invitations, credits } = detail.data;
  return (
    <Stack gap="lg">
      <Group gap="sm">
        <Title order={2}>{team.name}</Title>
        {team.held_by_staff ? <Badge variant="light">Waiting for its owner</Badge> : null}
        {team.deleted_at === null ? null : <Badge color="gray">Deleted</Badge>}
      </Group>
      <Group gap="xl">
        <Stack gap={0}>
          <Text size="sm" c="var(--cai-color-text-muted)">
            Available
          </Text>
          <Text fw={700} size="xl">
            {credits.balance.available} credits
          </Text>
        </Stack>
        <Stack gap={0}>
          <Text size="sm" c="var(--cai-color-text-muted)">
            Held for running jobs
          </Text>
          <Text fw={700} size="xl">
            {credits.balance.held} credits
          </Text>
        </Stack>
      </Group>
      {team.deleted_at === null ? (
        <SimpleGrid cols={{ base: 1, md: 3 }}>
          <GrantForm teamId={teamId} />
          <AdjustForm teamId={teamId} />
          {cancel}
        </SimpleGrid>
      ) : (
        <Stack gap="xs">
          {/* A hint: the API refuses credits on a deleted team, and still cancels its jobs. */}
          <Text size="sm">This team was deleted, so its credits no longer change.</Text>
          {cancel}
        </Stack>
      )}
      <Stack gap="xs">
        <Title order={3} size="h4">
          Recent credits
        </Title>
        {credits.entries.length === 0 ? <Text size="sm">No entries yet.</Text> : null}
        {credits.entries.length === 0 ? null : (
          <LedgerTable entries={credits.entries} onCancelJob={toCancel} />
        )}
      </Stack>
      <Stack gap="xs">
        <Title order={3} size="h4">
          Members
        </Title>
        <Table.ScrollContainer minWidth={560}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Role</Table.Th>
                <Table.Th>Joined</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {members.map((member) => (
                <Table.Tr key={member.user_id}>
                  <Table.Td>{member.name ?? member.email ?? "Unknown user"}</Table.Td>
                  <Table.Td>{member.email ?? ""}</Table.Td>
                  <Table.Td>{member.role}</Table.Td>
                  <Table.Td>{formatDate(member.joined_at)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Stack>
      <Stack gap="xs">
        <Title order={3} size="h4">
          Open invitations
        </Title>
        {invitations.length === 0 ? <Text size="sm">None.</Text> : null}
        {invitations.map((invitation) => (
          <Text key={invitation.id} size="sm">
            {invitation.email}, {invitation.role}
            {invitation.expired ? " (expired)" : `, until ${formatDate(invitation.expires_at)}`}
          </Text>
        ))}
      </Stack>
    </Stack>
  );
}
